import type { GlsLaunchParams } from '@/game-client/hooks/use-gls';
import { buildGlsLaunchParamsFromStore } from '@/game-client/helpers/build-gls-launch-params-from-store';
import {
  applySoftReloadPreset,
  SOFT_RELOAD_PRESET,
} from '@/lostHooks/softReload/softReloadPresets';
import { setGameStatus } from '@/store/actions';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { ddNotifyJackpotReconDelivery } from '@/utilities/logger/datadog/rumEvents/jackpotRecon';
import { GlsInitResponse } from '@/api/gls/types';

export type FetchGlsGame = (
  params: GlsLaunchParams,
  shouldSkipPrismic?: boolean,
  prefetchedResponse?: GlsInitResponse,
) => Promise<void>;

export type JackpotGlsReconcileLaunchVitals = 'preserve' | 'abortAndRestart';

export type JackpotGlsReconcileOptions = {
  shouldResetLoadingScreen?: boolean;
  /** Auto reconciliation keeps the original launch envelope; user opt-out restarts vitals. */
  launchVitals?: JackpotGlsReconcileLaunchVitals;
};

const CONTINUE_WITHOUT_JACKPOTS_VITALS_ERROR = {
  errorType: 'jackpot_continue_without',
  errorMessage: 'User continued without site-wide jackpots',
} as const;

/**
 * Jackpot reconcile: soft-reload via {@link SOFT_RELOAD_PRESET.JACKPOT_RETRY}
 * (clears GLS payload + invalidates launch in the preset), then refetch GLS.
 */
export const runJackpotGlsReconcile = async (
  fetchGame: FetchGlsGame,
  urlGameUid?: string | null,
  options: JackpotGlsReconcileOptions = {},
  /** First GLS already loaded game metadata; skip Prismic/Redis on the relaunch. */
  shouldSkipPrismic = true,
): Promise<void> => {
  const { shouldResetLoadingScreen = false, launchVitals = 'preserve' } =
    options;
  const preserveLaunchVitals = launchVitals === 'preserve';

  const params = buildGlsLaunchParamsFromStore(urlGameUid);
  if (!params) {
    Logger.logRumError({
      name: '[GW][RECON] GLS_JACKPOT_RECONCILIATION_MISSING_LAUNCH_PARAMS',
      useCase: DdEventCategory.GameLaunch,
      eventType: DdEventType.Error,
    });
    return;
  }

  setGameStatus({ jackpotReconcileRunning: true });

  try {
    await applySoftReloadPreset(
      SOFT_RELOAD_PRESET.JACKPOT_RETRY,
      false,
      preserveLaunchVitals,
      shouldResetLoadingScreen,
      preserveLaunchVitals ? undefined : CONTINUE_WITHOUT_JACKPOTS_VITALS_ERROR,
    );

    await fetchGame(
      {
        ...params,
        jackpotMode: false,
      },
      shouldSkipPrismic,
      undefined,
    );

    ddNotifyJackpotReconDelivery({ jackpotMode: false });
  } finally {
    setGameStatus({ jackpotReconcileRunning: false });
  }
};
